import { useMemo } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { FileText, FolderKanban, ListChecks, Sparkles } from 'lucide-react';
import type { Severity } from '@seo/types';
import { PageHeader } from '@/components/page-header';
import { GlassSurface } from '@/components/glow-card';
import { ErrorBanner } from '@/components/error-banner';
import { Spinner } from '@/components/spinner';
import { StatusPill, humanStatus } from '@/components/status-pill';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DetailError } from '@/components/detail-page';
import { fadeUp, stagger } from '@/components/motion';
import { useCompetitorRun } from '@/features/competitors/use-competitor-run';
import { useCompetitorFindings } from '@/features/competitors/use-competitor-findings';
import { useProject } from '@/features/projects/use-project';

const SEVERITY_VARIANT: Record<Severity, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  critical: 'destructive',
  high: 'destructive',
  medium: 'default',
  low: 'secondary',
};

export function CompetitorRunDetailRoute(): JSX.Element {
  const { runId } = useParams({ strict: false }) as { runId: string };
  const run = useCompetitorRun(runId);
  const findings = useCompetitorFindings(runId);

  const grouped = useMemo(() => {
    const items = findings.data?.items ?? [];
    const groups = new Map<string, typeof items>();
    for (const f of items) {
      if (!groups.has(f.type)) groups.set(f.type, []);
      groups.get(f.type)!.push(f);
    }
    return [...groups.entries()];
  }, [findings.data]);

  if (run.error) {
    return (
      <div className="space-y-4">
        <DetailError
          error={run.error}
          entityKind="competitor scan"
          backTo="/competitors"
          backLabel="Competitors"
        />
      </div>
    );
  }
  if (run.isLoading || !run.data) {
    return (
      <div className="flex justify-center p-10">
        <Spinner />
      </div>
    );
  }

  const r = run.data;

  return (
    <motion.div className="space-y-6" initial="hidden" animate="show" variants={stagger}>
      <PageHeader
        title={`Competitor scan — ${new Date(r.created_at).toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })}`}
        eyebrow="Competitors"
        description={`Checking ${r.competitor_ids.length} competitor${r.competitor_ids.length === 1 ? '' : 's'} for new pages, ranking changes, and backlinks. · ${humanStatus(r.status)}`}
        actions={
          <div className="flex items-center gap-2">
            <ProjectLink projectId={r.project_id} />
            <StatusPill status={r.status} />
          </div>
        }
      />

      {r.digest_md ? (
        <motion.section variants={fadeUp}>
          <GlassSurface className="p-5">
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
              <Sparkles className="h-4 w-4" /> What changed
            </div>
            <pre className="whitespace-pre-wrap break-words font-sans text-sm text-foreground/95">
{r.digest_md}
            </pre>
          </GlassSurface>
        </motion.section>
      ) : null}

      {r.failure_reason ? (
        <motion.section variants={fadeUp}>
          <GlassSurface className="border-rose-500/30 bg-rose-500/5 p-4 text-sm text-rose-200">
            {r.failure_reason}
          </GlassSurface>
        </motion.section>
      ) : null}

      <motion.section variants={fadeUp} className="space-y-4">
        <div className="flex items-center gap-2">
          <ListChecks className="h-4 w-4" />
          <h2 className="text-lg font-semibold">What we found ({findings.data?.items.length ?? 0})</h2>
          {findings.isFetching ? <Spinner /> : null}
        </div>
        {findings.error ? <ErrorBanner error={findings.error} /> : null}
        {grouped.length === 0 ? (
          <GlassSurface className="p-6 text-sm text-muted-foreground">
            {r.status === 'completed' ? "Nothing new to report from this scan." : "We're still scanning — findings will appear here."}
          </GlassSurface>
        ) : (
          grouped.map(([type, rows]) => (
            <GlassSurface key={type} className="p-4">
              <div className="mb-3 flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                  {type.replace(/_/g, ' ')} ({rows.length})
                </h3>
              </div>
              <ul className="space-y-2">
                {rows.map((f) => (
                  <li
                    key={f.id}
                    className="rounded-md border border-border/60 bg-background/40 p-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <Badge variant={SEVERITY_VARIANT[f.severity]}>{f.severity}</Badge>
                          <div className="font-medium text-foreground">{f.title}</div>
                        </div>
                        {f.detail ? (
                          <p className="mt-1 text-xs text-muted-foreground">{f.detail}</p>
                        ) : null}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </GlassSurface>
          ))
        )}
      </motion.section>
    </motion.div>
  );
}

/** Small "open the owning project" affordance; also primes the breadcrumb. */
function ProjectLink({ projectId }: { projectId: string }): JSX.Element | null {
  const project = useProject(projectId);
  if (!projectId) return null;
  return (
    <Button asChild variant="outline" size="sm">
      <Link to="/projects/$projectId" params={{ projectId }}>
        <FolderKanban className="h-3.5 w-3.5" />
        {project.data?.name ?? 'Open project'}
      </Link>
    </Button>
  );
}
